import { useEffect, useRef } from 'react'

import InfiniteScroll from 'react-infinite-scroller'

import { Notification } from '@tribeplatform/gql-client/types'
import {
  useClearNotificationsCount,
  useNotifications,
  useReadNotification,
} from '@tribeplatform/react-sdk/hooks'
import { simplifyPaginatedResult } from '@tribeplatform/react-sdk/utils'

import { useNavigate } from '../Link/index.js'

import { NotificationDropdownLoading } from './NotificationDropdownLoading.js'
import { NotificationEmptyState } from './NotificationEmptyState.js'
import { getLink, NotificationRow } from './NotificationRow.js'

interface Props {
  closeDropdown: () => void
}
export const NotificationDropdownList = ({ closeDropdown }: Props) => {
  const scrollParentRef = useRef<HTMLDivElement>()
  const navigate = useNavigate()
  const {
    data,
    fetchNextPage,
    isFetching,
    hasNextPage = false,
  } = useNotifications({
    fields: {
      actor: { media: 'basic' },
      object: 'basic',
      target: 'basic',
    },
    variables: { limit: 10 },
    useInfiniteQueryOptions: {
      notifyOnChangeProps: ['isFetching', 'data', 'hasNextPage'],
    },
  })
  const { mutate: clearNotificationCount } = useClearNotificationsCount()

  const { mutate: readNotification } = useReadNotification()

  const { nodes: notifications } = simplifyPaginatedResult<Notification>(data)

  useEffect(() => {
    clearNotificationCount()
  }, [clearNotificationCount])

  return (
    <div className="overflow-y-auto max-h-96" ref={scrollParentRef}>
      <InfiniteScroll
        pageStart={0}
        loadMore={fetchNextPage}
        hasMore={hasNextPage || false}
        useWindow={false}
        getScrollParent={() => scrollParentRef.current}
      >
        <ul className="divide-y divide-line-subdued">
          {notifications &&
            notifications
              .filter(({ meta }) => !!meta)
              .map(notification => (
                <li key={notification.id} className="p-3">
                  <div
                    className="hover:bg-surface-hovered space-s-3 p-3 cursor-pointer rounded-base transition duration-200 focus:outline-none focus-visible:ring block"
                    onClick={() => {
                      readNotification({ notificationId: notification.id })
                      closeDropdown()
                      navigate(getLink(notification))
                    }}
                  >
                    <NotificationRow notification={notification} />
                  </div>
                </li>
              ))}
          {isFetching && <NotificationDropdownLoading />}
          {!notifications.length && !isFetching && (
            <li className="py-10">
              <NotificationEmptyState />
            </li>
          )}
        </ul>
      </InfiniteScroll>
    </div>
  )
}
